import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
  componentName?: string;
}

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Load failed for module',
];

function isDynamicImportError(error: Error): boolean {
  const message = `${error.message || ''}\n${error.stack || ''}`;
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) =>
    message.toLowerCase().includes(pattern.toLowerCase()),
  );
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Extract component name from stack
    const stackLines = errorInfo.componentStack?.split('\n') || [];
    const firstComponent = stackLines.find(l => l.trim().startsWith('at '))?.trim() || 'unknown';
    
    console.error('[ErrorBoundary] Component:', firstComponent);
    console.error('[ErrorBoundary] Error:', error.message);
    console.error('[ErrorBoundary] Stack:', error.stack);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    if (isDynamicImportError(error)) {
      const reloadKey = 'lapyno-dynamic-import-reloaded';
      const alreadyReloaded = sessionStorage.getItem(reloadKey) === 'true';

      if (!alreadyReloaded) {
        sessionStorage.setItem(reloadKey, 'true');
        window.location.reload();
        return;
      }
    } else {
      sessionStorage.removeItem('lapyno-dynamic-import-reloaded');
    }

    // Auto-retry once
    if (this.state.retryCount === 0) {
      console.warn('[ErrorBoundary] Auto-retrying render...');
      setTimeout(() => {
        this.setState({ hasError: false, error: undefined, retryCount: 1 });
      }, 100);
    } else {
      this.setState({ componentName: firstComponent });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      
      const relevantStack = (this.state.error?.stack?.split('\n') || []).slice(0, 5).join('\n');
      
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
          <AlertTriangle className="h-10 w-10 text-amber-500" />
          <div className="text-center space-y-1">
            <h3 className="font-semibold text-foreground">Une erreur est survenue</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              {this.state.error?.message || 'Erreur inattendue'}
            </p>
            {this.state.componentName && (
              <p className="text-xs text-destructive font-mono mt-1">
                Composant: {this.state.componentName}
              </p>
            )}
            <details className="mt-2 text-left">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                Détails techniques (pour le support)
              </summary>
              <pre className="mt-2 text-[10px] text-muted-foreground bg-muted p-2 rounded max-w-lg overflow-auto max-h-32 whitespace-pre-wrap">
                {relevantStack}
              </pre>
            </details>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              this.setState({ hasError: false, error: undefined, retryCount: 0 });
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Réessayer
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Recharger la page
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}